import { useQuery } from '@tanstack/react-query'
import type { File as FileResource, StreamTokenOutput } from '@terafile/shared'
import { useEffect, useRef } from 'react'
import { apiFetch } from '../lib/api.js'
import { useAuth } from '../lib/auth-context.js'
import { SUBMIT_ERROR_FALLBACK, toUserMessage } from '../lib/error-copy.js'
import { LOADING, STREAM_STATUS_LABEL } from '../lib/labels.js'
import { Watermark } from './Watermark.js'

type Props = {
  file: FileResource
}

/**
 * 動画ビューワー（Cloudflare Stream iframe）。
 *
 * - `streamStatus !== 'READY'` のときはプレースホルダ表示
 * - `READY` で `/files/:id/stream-token` を叩いて iframe URL を取得
 * - 再生イベント（progress 30 秒毎・end）を `/files/:id/playback-events` に通知
 */
export function VideoPlayer({ file }: Props) {
  const ready = file.streamStatus === 'READY' && !!file.streamUid

  // Stream の playback token は寿命が短い（動画長 + 30 分程度）。
  // React Query がキャッシュした古いトークンを iframe に渡すと
  // Cloudflare Stream で「コンテンツがブロックされました」になるため、
  // 開き直すたびに必ず新しいトークンを取得する。
  const { data, error } = useQuery<StreamTokenOutput>({
    queryKey: ['stream-token', file.id],
    queryFn: () =>
      apiFetch<StreamTokenOutput>(`/files/${file.id}/stream-token`, { method: 'POST' }),
    enabled: ready,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    retry: 1,
  })

  if (!ready) {
    return <PlaceholderByStatus status={file.streamStatus} />
  }
  if (error) {
    const msg = toUserMessage(error, SUBMIT_ERROR_FALLBACK)
    return <p className="text-sm text-red-600">{msg}</p>
  }
  if (!data) {
    return <p className="text-sm text-muted-foreground">{LOADING.fetching}</p>
  }

  return <StreamIframe fileId={file.id} iframeUrl={data.iframeUrl} />
}

function PlaceholderByStatus({ status }: { status: FileResource['streamStatus'] }) {
  if (status === 'PENDING' || status === 'QUEUED') {
    return (
      <div className="aspect-video bg-muted flex items-center justify-center rounded-md">
        <p className="text-sm text-muted-foreground">{STREAM_STATUS_LABEL.PENDING}</p>
      </div>
    )
  }
  if (status === 'ERROR') {
    return (
      <div className="aspect-video bg-red-50 border border-red-200 flex items-center justify-center rounded-md">
        <p className="text-sm text-red-700">{STREAM_STATUS_LABEL.ERROR}</p>
      </div>
    )
  }
  // streamUid 未発行 (null) など。「準備中」として扱う
  return (
    <div className="aspect-video bg-muted flex items-center justify-center rounded-md">
      <p className="text-sm text-muted-foreground">{STREAM_STATUS_LABEL.PENDING}</p>
    </div>
  )
}

function StreamIframe({ fileId, iframeUrl }: { fileId: string; iframeUrl: string }) {
  // ウォーターマーク用の識別子（オーナー時は email or user.id 短縮）
  const { session } = useAuth()
  // メアド等の PII は焼かず、user.id 短縮で追跡可能性を維持しつつ
  // 先頭にサービス名を入れて流出時のブランド露出も兼ねる。
  const viewerTag = session?.user.id?.slice(0, 8) ?? 'viewer'
  const watermarkLabel = `テラファイル便 · ${viewerTag}`

  // 再生開始は session 内 1 度だけ送る
  const startedRef = useRef(false)
  // 30 秒間隔で progress を送るための前回送信位置
  const lastReportedSecRef = useRef(0)
  // 累計視聴秒数を timeupdate ベースで概算（playing 中の delta を加算）
  const playedSecondsRef = useRef(0)
  const lastTimeRef = useRef(0)
  const isPlayingRef = useRef(false)

  useEffect(() => {
    // Cloudflare Stream Player は postMessage で再生状態を通知する。
    // origin は customer-* サブドメインだが、ここでは event.data の形だけで判定する。
    function onMessage(event: MessageEvent) {
      const data = event.data as { event?: string; currentTime?: number } | undefined | null
      if (!data || typeof data !== 'object' || !data.event) return
      const sec = Math.floor(data.currentTime ?? 0)

      switch (data.event) {
        case 'play':
        case 'playing': {
          isPlayingRef.current = true
          lastTimeRef.current = sec
          if (!startedRef.current) {
            startedRef.current = true
            void postPlaybackEvent(fileId, {
              event: 'start',
              playPositionSec: sec,
            })
          }
          break
        }
        case 'pause': {
          isPlayingRef.current = false
          break
        }
        case 'timeupdate': {
          // playing 中なら delta を累計に加算（巻き戻し時は加算しない）
          if (isPlayingRef.current && sec > lastTimeRef.current) {
            playedSecondsRef.current += sec - lastTimeRef.current
          }
          lastTimeRef.current = sec
          // 30 秒進んだら progress を送る
          if (sec - lastReportedSecRef.current >= 30) {
            lastReportedSecRef.current = sec
            void postPlaybackEvent(fileId, {
              event: 'progress',
              playPositionSec: sec,
              playedSeconds: playedSecondsRef.current,
            })
          }
          break
        }
        case 'seeked': {
          // シーク後の位置基準にリセット
          lastTimeRef.current = sec
          lastReportedSecRef.current = sec
          void postPlaybackEvent(fileId, { event: 'seek', playPositionSec: sec })
          break
        }
        case 'ended': {
          isPlayingRef.current = false
          void postPlaybackEvent(fileId, {
            event: 'end',
            playPositionSec: sec,
            playedSeconds: playedSecondsRef.current,
          })
          break
        }
        default:
          break
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [fileId])

  // unmount 時に最終 progress を送る（モーダルを閉じた等で end が来ない経路の保険）
  useEffect(() => {
    return () => {
      if (playedSecondsRef.current > 0 && lastTimeRef.current > 0) {
        void postPlaybackEvent(fileId, {
          event: 'progress',
          playPositionSec: lastTimeRef.current,
          playedSeconds: playedSecondsRef.current,
        })
      }
    }
  }, [fileId])

  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-md border border-border">
      <iframe
        src={iframeUrl}
        title="動画プレーヤー"
        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
        allowFullScreen
        className="w-full h-full"
      />
      {/* ウォーターマーク: スクリーンキャプチャ追跡用（F-PREV-12） */}
      <Watermark identifier={watermarkLabel} />
    </div>
  )
}

async function postPlaybackEvent(
  fileId: string,
  payload: {
    event: 'start' | 'progress' | 'seek' | 'end'
    playPositionSec: number
    playedSeconds?: number
  },
): Promise<void> {
  try {
    await apiFetch(`/files/${fileId}/playback-events`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  } catch {
    // 再生イベントの欠落はクリティカルではない（ログ収集の精度劣化のみ）
  }
}
